import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useSearchParams } from 'react-router-dom'
import Stack from '@mui/joy/Stack'
import Box from '@mui/joy/Box'
import Sheet from '@mui/joy/Sheet'
import Typography from '@mui/joy/Typography'
import Chip from '@mui/joy/Chip'
import Button from '@mui/joy/Button'
import Input from '@mui/joy/Input'
import IconButton from '@mui/joy/IconButton'
import Table from '@mui/joy/Table'
import Alert from '@mui/joy/Alert'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import Tooltip from '@mui/joy/Tooltip'
import FormLabel from '@mui/joy/FormLabel'
import FormHelperText from '@mui/joy/FormHelperText'
import RefreshIcon from '@mui/icons-material/Refresh'
import SyncIcon from '@mui/icons-material/Sync'
import ReplayIcon from '@mui/icons-material/Replay'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import MicIcon from '@mui/icons-material/Mic'
import NoteIcon from '@mui/icons-material/Note'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CloseIcon from '@mui/icons-material/Close'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BlockIcon from '@mui/icons-material/Block'
import DeleteIcon from '@mui/icons-material/Delete'

import {
    AttioMessage,
    AttioMessageStatus,
    AttioStatus,
    AttioCallItem,
    AttioCallRecording,
    AttioTranscriptSegment,
    AttioIgnoreRule,
    AttioIgnoreRuleType,
    getAttioStatus,
    listAttioMessages,
    retryAttioMessages,
    prepAttioCalls,
    resetAttioData,
    listAttioCalls,
    getAttioCallRecordings,
    getAttioCallTranscript,
    listAttioIgnoreRules,
    createAttioIgnoreRule,
    deleteAttioIgnoreRule,
    previewAttioBatch,
    syncAttioCall,
} from 'api/oracleApi'
import { useProcessJobContext } from '../../hooks/ProcessJobContext'
import { EtlPreviewModal, ScienceIcon } from '../shared/EtlPreviewModal'

// ── Constants ──────────────────────────────────────────────────────────────────

const CALLS_WINDOW_DAYS = 30
const EARLIEST_DATE = new Date('2025-01-01T00:00:00.000Z')

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtWindowRange(start: Date, end: Date): string {
    const thisYear = new Date().getFullYear()
    const fmt = (d: Date, showYear: boolean) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(showYear ? { year: 'numeric' } : {}) })
    const crossesYear = start.getFullYear() !== end.getFullYear()
    const notThisYear = end.getFullYear() !== thisYear
    return crossesYear ? `${fmt(start, true)} – ${fmt(end, true)}` : `${fmt(start, false)} – ${fmt(end, notThisYear)}`
}

// ── Types ──────────────────────────────────────────────────────────────────────

// "not_synced" is a UI-only state meaning the call exists in Attio but has
// no row in ds-attio-messages yet.
// "no_recording" is a UI-only state meaning the call has no completed recording
// and will never enter the pipeline.
type EffectiveOracleStatus = 'not_synced' | 'no_recording' | AttioMessageStatus
type StatusFilter = 'all' | EffectiveOracleStatus

// ── Status config ──────────────────────────────────────────────────────────────

const STATUS_CHIP_COLOR: Record<EffectiveOracleStatus, 'neutral' | 'primary' | 'success' | 'warning' | 'danger'> = {
    not_synced: 'neutral',
    no_recording: 'neutral',
    ready: 'primary',
    in_crm: 'success',
    unattributed: 'warning',
    failed: 'danger',
    jibberish: 'warning',
}

// Each status: what to show in chips/filters, and the tooltip explaining WHY.
const STATUS_META: Record<EffectiveOracleStatus, { label: string; tooltip: string; Icon: typeof HourglassEmptyIcon }> = {
    not_synced: {
        label: 'not synced',
        tooltip: 'This call exists in Attio but has no row in our system yet. Use "Ad hoc sync" to prep it.',
        Icon: CloudOffIcon,
    },
    no_recording: {
        label: 'no recording',
        tooltip: 'No completed recording found for this call — it will never enter the CRM pipeline.',
        Icon: BlockIcon,
    },
    ready: {
        label: 'ready',
        tooltip: 'Prepped in ds-attio-messages and ready for the CRM pipeline (Extract → Match → Commit).',
        Icon: HourglassEmptyIcon,
    },
    in_crm: {
        label: 'in CRM',
        tooltip: 'Successfully processed — company matched, facts written to the CRM knowledge base.',
        Icon: CheckCircleIcon,
    },
    unattributed: {
        label: 'unattributed',
        tooltip: 'Pipeline ran but no matching company could be found. Consider retrying after adding company data.',
        Icon: WarningAmberIcon,
    },
    failed: {
        label: 'failed',
        tooltip: 'An error occurred during processing. Use "Retry failed" to re-queue, or check logs.',
        Icon: ErrorOutlineIcon,
    },
    jibberish: {
        label: 'jibberish',
        tooltip: 'STT failed to detect the spoken language — transcript is garbled. This call will never enter the CRM pipeline.',
        Icon: ErrorOutlineIcon,
    },
}

// ── Helpers ────────────────────────────────────────────────────────────────────

// Derives the effective CRM status for a call given its linked messages.
// Priority: failed > unattributed > ready > in_crm > not_synced.
function computeCallOracleStatus(msgs: AttioMessage[]): EffectiveOracleStatus {
    if (msgs.length === 0) return 'not_synced'
    if (msgs.some((m) => m.status === 'failed')) return 'failed'
    if (msgs.some((m) => m.status === 'unattributed')) return 'unattributed'
    if (msgs.some((m) => m.status === 'ready')) return 'ready'
    if (msgs.every((m) => m.status === 'jibberish')) return 'jibberish'
    return 'in_crm'
}

// Extracts the Attio meeting_id from a crm_attio_messages entry_id.
// entry_id format: "call_{meetingId}_{recordingId}" — both IDs are UUID-like
// (hyphens, no underscores), so the first underscore after "call_" is the sep.
function meetingIdFromEntryId(entryId: string): string | null {
    if (!entryId.startsWith('call_')) return null
    const body = entryId.slice('call_'.length)
    const sep = body.indexOf('_')
    return sep === -1 ? null : body.slice(0, sep)
}

function relativeTime(iso: string | null): string {
    if (!iso) return '—'
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'just now'
    if (mins < 60) return `${mins}m ago`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `${hrs}h ago`
    return `${Math.floor(hrs / 24)}d ago`
}

function callStartDatetime(call: AttioCallItem): string | null {
    const s = call.start
    if ('datetime' in s) return s.datetime
    if ('date' in s) return s.date
    return null
}

function callEndDatetime(call: AttioCallItem): string | null {
    if (!call.end) return null
    const e = call.end
    if ('datetime' in e) return e.datetime
    if ('date' in e) return e.date
    return null
}

function fmtCallDate(iso: string | null): string {
    if (!iso) return '—'
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function fmtDuration(start: string | null, end: string | null): string {
    if (!start || !end) return '—'
    const ms = new Date(end).getTime() - new Date(start).getTime()
    if (ms <= 0) return '—'
    const totalMins = Math.floor(ms / 60000)
    const hrs = Math.floor(totalMins / 60)
    const mins = totalMins % 60
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`
}

function fmtSeconds(totalSecs: number): string {
    if (totalSecs <= 0) return '0m'
    const totalMins = Math.floor(totalSecs / 60)
    const secs = Math.floor(totalSecs % 60)
    if (totalMins >= 60) {
        const hrs = Math.floor(totalMins / 60)
        const remMins = totalMins % 60
        return `${hrs}h ${remMins}m`
    }
    return secs > 0 ? `${totalMins}m ${secs}s` : `${totalMins}m`
}

function collapseSegments(segments: AttioTranscriptSegment[]): string {
    const lines: string[] = []
    let currentSpeaker = ''
    let currentParts: string[] = []
    const flush = () => {
        if (!currentSpeaker && currentParts.length === 0) return
        const utterance = currentParts
            .join(' ')
            .replace(/\s+([,.!?;:])/g, '$1')
            .trim()
        if (utterance) lines.push(`${currentSpeaker}: ${utterance}`)
        currentParts = []
    }
    for (const s of segments) {
        const speaker = s.speaker.name || 'Unknown'
        const speech = (s.speech || '').trim()
        if (!speech) continue
        if (speaker !== currentSpeaker) {
            flush()
            currentSpeaker = speaker
        }
        currentParts.push(speech)
    }
    flush()
    return lines.join('\n')
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <Typography level="body-xs" sx={{ textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600, color: 'text.tertiary', fontSize: 10 }}>
            {children}
        </Typography>
    )
}

function OracleStatusChip({ status }: { status: EffectiveOracleStatus }) {
    const { label, tooltip, Icon } = STATUS_META[status]
    return (
        <Tooltip title={tooltip} placement="bottom">
            <Chip size="sm" color={STATUS_CHIP_COLOR[status]} variant="soft" startDecorator={<Icon sx={{ fontSize: 13 }} />}>
                {label}
            </Chip>
        </Tooltip>
    )
}

function InlineCounts({ counts }: { counts: AttioStatus['counts'] }) {
    const order: AttioMessageStatus[] = ['ready', 'in_crm', 'unattributed', 'failed', 'jibberish']
    return (
        <Stack direction="row" gap={0.75} alignItems="center">
            {order.map((key) => {
                const { label, tooltip, Icon } = STATUS_META[key]
                return (
                    <Tooltip key={key} title={tooltip} placement="bottom">
                        <Chip
                            size="sm"
                            color={STATUS_CHIP_COLOR[key]}
                            variant="soft"
                            startDecorator={<Icon sx={{ fontSize: 12 }} />}
                            sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 90, justifyContent: 'center' }}
                        >
                            <Box component="span" sx={{ fontWeight: 600 }}>
                                {counts[key]}
                            </Box>
                            <Box component="span" sx={{ ml: 0.5, opacity: 0.7, fontSize: 11 }}>
                                {label}
                            </Box>
                        </Chip>
                    </Tooltip>
                )
            })}
        </Stack>
    )
}

function InlineCursors({ cursors }: { cursors: AttioStatus['cursors'] }) {
    return (
        <Tooltip title="Last calls sync cursor" placement="bottom">
            <Typography level="body-xs" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.tertiary' }}>
                <Box component="span" sx={{ opacity: 0.7 }}>
                    calls{' '}
                </Box>
                {relativeTime(cursors.meetings_cursor)}
            </Typography>
        </Tooltip>
    )
}

// Status filter pills — single-select, shows count for "not synced".
function StatusFilterPills({ value, onChange, notSyncedCount }: { value: StatusFilter; onChange: (v: StatusFilter) => void; notSyncedCount: number }) {
    const filters: { key: StatusFilter; label: string }[] = [
        { key: 'all', label: 'All' },
        { key: 'not_synced', label: `Not synced${notSyncedCount > 0 ? ` · ${notSyncedCount}` : ''}` },
        { key: 'ready', label: 'Ready' },
        { key: 'in_crm', label: 'In CRM' },
        { key: 'unattributed', label: 'Unattributed' },
        { key: 'failed', label: 'Failed' },
        { key: 'jibberish', label: 'Jibberish' },
    ]
    return (
        <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mr: 0.25 }}>
                Filter:
            </Typography>
            {filters.map(({ key, label }) => {
                const active = key === value
                return (
                    <Chip
                        key={key}
                        size="sm"
                        color={active ? (key === 'all' ? 'neutral' : STATUS_CHIP_COLOR[key as EffectiveOracleStatus]) : 'neutral'}
                        variant={active ? 'solid' : 'soft'}
                        onClick={() => onChange(key)}
                        sx={{ cursor: 'pointer' }}
                    >
                        {label}
                    </Chip>
                )
            })}
        </Stack>
    )
}

// ── CallRow ────────────────────────────────────────────────────────────────────

const RECORDING_STATUS_COLORS = { completed: 'success', processing: 'warning', failed: 'danger' } as const

interface CallRowProps {
    call: AttioCallItem
    linkedMessages: AttioMessage[]
    isIgnored: boolean
    isCandidate: boolean | 'loading'
    onIgnore: (type: AttioIgnoreRuleType, value: string, label: string) => Promise<void>
    onSync: (meetingId: string, force: boolean) => Promise<void>
}

function CallRow({ call, linkedMessages, isIgnored, isCandidate, onIgnore, onSync }: CallRowProps) {
    const [expanded, setExpanded] = useState(false)
    const [recordings, setRecordings] = useState<AttioCallRecording[]>([])
    const [recordingsLoading, setRecordingsLoading] = useState(false)
    const [recordingsFetched, setRecordingsFetched] = useState(false)
    const [transcripts, setTranscripts] = useState<Record<string, AttioTranscriptSegment[]>>({})
    const [transcriptLoading, setTranscriptLoading] = useState<Record<string, boolean>>({})
    const [ignoreModalOpen, setIgnoreModalOpen] = useState(false)
    const [ignoring, setIgnoring] = useState(false)
    const [adHocSyncing, setAdHocSyncing] = useState(false)

    const start = callStartDatetime(call)
    const end = callEndDatetime(call)
    const companyLink = call.linked_records.find((lr) => lr.object_slug === 'companies')
    const baseCrmStatus = computeCallOracleStatus(linkedMessages)
    const crmStatus: EffectiveOracleStatus = baseCrmStatus === 'not_synced' && isCandidate === false ? 'no_recording' : baseCrmStatus

    const completedRecordingIds = recordings.filter((r) => r.status === 'completed').map((r) => r.id.call_recording_id)
    const allTranscriptsDone =
        recordingsFetched && completedRecordingIds.length > 0 && completedRecordingIds.every((id) => id in transcripts && !transcriptLoading[id])
    const actualDurationSecs = allTranscriptsDone ? Math.max(0, ...completedRecordingIds.flatMap((id) => transcripts[id] ?? []).map((s) => s.end_time)) : null

    // Company hint: prefer the one from linked messages (enriched by us), fall
    // back to Attio's raw company record_id hint if available.
    const companyHint =
        linkedMessages[0]?.external_attribution?.nameHint ?? companyLink?.name ?? (companyLink ? `(id: ${companyLink.record_id.slice(0, 8)}…)` : null)

    const handleToggle = async () => {
        const next = !expanded
        setExpanded(next)
        if (next && !recordingsFetched) {
            setRecordingsLoading(true)
            setRecordingsFetched(true)
            try {
                const recs = await getAttioCallRecordings(call.id.meeting_id)
                setRecordings(recs)
                const completed = recs.filter((r) => r.status === 'completed')
                if (completed.length > 0) {
                    const ids = completed.map((r) => r.id.call_recording_id)
                    setTranscriptLoading(Object.fromEntries(ids.map((id) => [id, true])))
                    await Promise.all(
                        completed.map(async (rec) => {
                            const recId = rec.id.call_recording_id
                            try {
                                const segments = await getAttioCallTranscript(call.id.meeting_id, recId)
                                setTranscripts((prev) => ({ ...prev, [recId]: segments }))
                            } catch {
                                setTranscripts((prev) => ({ ...prev, [recId]: [] }))
                            } finally {
                                setTranscriptLoading((prev) => ({ ...prev, [recId]: false }))
                            }
                        })
                    )
                }
            } catch {
                // silently handled — recordings section stays empty
            } finally {
                setRecordingsLoading(false)
            }
        }
    }

    const handleIgnoreSelect = async (type: AttioIgnoreRuleType) => {
        const value = type === 'title_exact' ? (call.title ?? '') : call.id.meeting_id
        const label = type === 'title_exact' ? `All calls titled "${call.title}"` : `Call ${call.title ?? call.id.meeting_id.slice(0, 8) + '…'} (specific)`
        setIgnoring(true)
        try {
            await onIgnore(type, value, label)
        } finally {
            setIgnoring(false)
            setIgnoreModalOpen(false)
        }
    }

    return (
        <>
            <tr
                style={{
                    cursor: 'pointer',
                    opacity: isIgnored ? 0.45 : 1,
                    background: isCandidate === true && !isIgnored ? 'var(--joy-palette-success-softBg)' : undefined,
                }}
                onClick={handleToggle}
            >
                <td>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        <MicIcon fontSize="small" sx={{ color: isIgnored ? 'neutral.400' : 'primary.400', flexShrink: 0 }} />
                        <Typography
                            level="body-sm"
                            sx={{
                                fontWeight: call.title ? 'md' : 'normal',
                                fontStyle: call.title ? 'normal' : 'italic',
                                color: call.title ? 'text.primary' : 'text.tertiary',
                                textDecoration: isIgnored ? 'line-through' : 'none',
                            }}
                        >
                            {call.title || 'Untitled call'}
                        </Typography>
                        {isIgnored && (
                            <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: 10, '--Chip-paddingInline': '5px', '--Chip-minHeight': '16px' }}>
                                ignored
                            </Chip>
                        )}
                    </Stack>
                </td>
                <td>
                    <Typography level="body-xs" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtCallDate(start)}
                    </Typography>
                </td>
                <td>
                    <Typography level="body-xs" color="neutral">
                        {fmtDuration(start, end)}
                    </Typography>
                </td>
                <td>
                    <Chip size="sm" variant="soft" color={call.participants.length > 0 ? 'primary' : 'neutral'} sx={{ minWidth: 32, justifyContent: 'center' }}>
                        {call.participants.length}
                    </Chip>
                </td>
                <td>
                    {companyHint ? (
                        <Typography level="body-xs" color="neutral" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {companyHint}
                        </Typography>
                    ) : (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                            unknown
                        </Typography>
                    )}
                </td>
                <td>
                    <OracleStatusChip status={crmStatus} />
                </td>
                <td>
                    <Stack direction="row" alignItems="center" gap={0.5} justifyContent="flex-end">
                        {!isIgnored && (
                            <Tooltip
                                title={
                                    crmStatus === 'not_synced'
                                        ? "Data prep: fetch this call's transcript from Attio and mark it ready for the CRM pipeline"
                                        : 'Force re-sync: delete existing record and re-run transcript quality check'
                                }
                                placement="left"
                            >
                                <IconButton
                                    size="sm"
                                    variant="plain"
                                    color={crmStatus === 'not_synced' ? 'primary' : 'neutral'}
                                    loading={adHocSyncing}
                                    onClick={async (e) => {
                                        e.stopPropagation()
                                        setAdHocSyncing(true)
                                        try {
                                            await onSync(call.id.meeting_id, crmStatus !== 'not_synced')
                                        } finally {
                                            setAdHocSyncing(false)
                                        }
                                    }}
                                    sx={{ opacity: 0.55, '&:hover': { opacity: 1 } }}
                                >
                                    {crmStatus === 'not_synced' ? <SyncIcon sx={{ fontSize: 15 }} /> : <ReplayIcon sx={{ fontSize: 15 }} />}
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={isIgnored ? 'This call is ignored — click to manage rules' : 'Ignore this call going forward'} placement="left">
                            <IconButton
                                size="sm"
                                variant="plain"
                                color={isIgnored ? 'danger' : 'neutral'}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    setIgnoreModalOpen(true)
                                }}
                                sx={{ opacity: isIgnored ? 1 : 0.35, '&:hover': { opacity: 1 } }}
                            >
                                <BlockIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                        </Tooltip>
                        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Stack>
                </td>
            </tr>

            {/* Ignore confirmation modal */}
            {ignoreModalOpen &&
                createPortal(
                    <Box
                        sx={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 1400,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(0,0,0,0.35)',
                        }}
                        onClick={() => setIgnoreModalOpen(false)}
                    >
                        <Sheet
                            variant="outlined"
                            sx={{ borderRadius: 'md', p: 2.5, minWidth: 340, maxWidth: 440, boxShadow: 'lg' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Stack gap={1.5}>
                                <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                                    <Stack gap={0.25}>
                                        <Typography level="title-sm">Ignore this call</Typography>
                                        <Typography level="body-xs" color="neutral">
                                            Ignored calls are skipped by the sync pipeline and never ingested into the CRM.
                                        </Typography>
                                    </Stack>
                                    <IconButton size="sm" variant="plain" color="neutral" onClick={() => setIgnoreModalOpen(false)} sx={{ mt: -0.5, mr: -0.5 }}>
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Stack>
                                <Divider />
                                <Stack gap={0.75}>
                                    {call.title && (
                                        <Button
                                            variant="outlined"
                                            color="warning"
                                            size="sm"
                                            loading={ignoring}
                                            startDecorator={<BlockIcon sx={{ fontSize: 15 }} />}
                                            onClick={() => handleIgnoreSelect('title_exact')}
                                            sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                        >
                                            <Stack alignItems="flex-start" gap={0.1}>
                                                <span>Ignore all calls titled "{call.title}"</span>
                                                <Typography level="body-xs" sx={{ opacity: 0.7, fontWeight: 'normal' }}>
                                                    Any future call with this exact title will be skipped
                                                </Typography>
                                            </Stack>
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="neutral"
                                        size="sm"
                                        loading={ignoring}
                                        startDecorator={<BlockIcon sx={{ fontSize: 15 }} />}
                                        onClick={() => handleIgnoreSelect('meeting_id')}
                                        sx={{ justifyContent: 'flex-start', textAlign: 'left' }}
                                    >
                                        <Stack alignItems="flex-start" gap={0.1}>
                                            <span>Ignore only this specific call</span>
                                            <Typography level="body-xs" sx={{ opacity: 0.7, fontWeight: 'normal', fontFamily: 'monospace' }}>
                                                {call.id.meeting_id}
                                            </Typography>
                                        </Stack>
                                    </Button>
                                </Stack>
                            </Stack>
                        </Sheet>
                    </Box>,
                    document.body
                )}

            {expanded && (
                <tr>
                    <td colSpan={7} style={{ padding: 0 }}>
                        <Box sx={{ px: 2, py: 1.25, bgcolor: 'background.level1', borderBottom: '1px solid', borderColor: 'divider' }}>
                            {/* ── meta strip ── */}
                            <Stack direction="row" alignItems="baseline" gap={3} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
                                <Stack direction="row" alignItems="center" gap={0.75}>
                                    <SectionLabel>Meeting ID</SectionLabel>
                                    <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                                        {call.id.meeting_id}
                                    </Typography>
                                    <Tooltip title="Open in Attio" placement="top">
                                        <Box
                                            component="a"
                                            href={`https://app.attio.com/numeo/calls/${call.id.meeting_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ display: 'flex', alignItems: 'center', color: 'text.tertiary', '&:hover': { color: 'primary.500' } }}
                                        >
                                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                                        </Box>
                                    </Tooltip>
                                </Stack>
                                {start && (
                                    <Stack direction="row" alignItems="baseline" gap={0.75}>
                                        <SectionLabel>Scheduled</SectionLabel>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                            {fmtCallDate(start)}
                                            {end ? ` → ${fmtCallDate(end)}` : ''}&ensp;·&ensp;{fmtDuration(start, end)}
                                        </Typography>
                                    </Stack>
                                )}
                                {actualDurationSecs !== null && (
                                    <Stack direction="row" alignItems="baseline" gap={0.75}>
                                        <SectionLabel>Recording</SectionLabel>
                                        <Typography
                                            level="body-xs"
                                            sx={{
                                                color: actualDurationSecs === 0 ? 'warning.600' : 'text.secondary',
                                                fontWeight: actualDurationSecs === 0 ? 'lg' : 'normal',
                                            }}
                                        >
                                            {actualDurationSecs === 0 ? '0m — no speech detected' : fmtSeconds(actualDurationSecs)}
                                        </Typography>
                                    </Stack>
                                )}
                                {call.linked_records.map((lr, i) => (
                                    <Stack key={i} direction="row" alignItems="center" gap={0.5}>
                                        <SectionLabel>{lr.object_slug}</SectionLabel>
                                        <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                            {lr.name ?? `(id: ${lr.record_id.slice(0, 8)}…)`}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        crmStatus !== 'not_synced' && linkedMessages.length > 0 ? '1fr 1fr 1fr' : recordings.length > 0 ? '1fr 1fr' : '1fr',
                                    gap: 2,
                                    alignItems: 'start',
                                }}
                            >
                                {/* Participants */}
                                <Stack gap={0.75}>
                                    <SectionLabel>Participants ({call.participants.length})</SectionLabel>
                                    {call.participants.length === 0 ? (
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                            No participants recorded
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '2px 12px' }}>
                                            {call.participants.map((p, i) => (
                                                <Stack key={i} direction="row" alignItems="center" gap={0.75} sx={{ py: '3px', overflow: 'hidden' }}>
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    >
                                                        {p.email_address || '—'}
                                                    </Typography>
                                                    <Stack direction="row" gap={0.4} sx={{ flexShrink: 0 }}>
                                                        <Chip
                                                            size="sm"
                                                            variant="soft"
                                                            color={p.status === 'accepted' ? 'success' : p.status === 'declined' ? 'danger' : 'neutral'}
                                                            sx={{ fontSize: 10, '--Chip-paddingInline': '6px', '--Chip-minHeight': '18px' }}
                                                        >
                                                            {p.status}
                                                        </Chip>
                                                        {p.is_organizer && (
                                                            <Chip
                                                                size="sm"
                                                                variant="outlined"
                                                                color="primary"
                                                                sx={{ fontSize: 10, '--Chip-paddingInline': '6px', '--Chip-minHeight': '18px' }}
                                                            >
                                                                org
                                                            </Chip>
                                                        )}
                                                    </Stack>
                                                </Stack>
                                            ))}
                                        </Box>
                                    )}
                                </Stack>

                                {/* Recordings + transcripts */}
                                {(recordings.length > 0 || recordingsLoading) && (
                                    <Stack gap={0.75}>
                                        <SectionLabel>Recordings</SectionLabel>
                                        {recordingsLoading ? (
                                            <CircularProgress size="sm" />
                                        ) : (
                                            <Stack gap={1}>
                                                {recordings.map((rec) => {
                                                    const recId = rec.id.call_recording_id
                                                    const hasTranscript = recId in transcripts
                                                    const isLoading = transcriptLoading[recId] ?? false
                                                    const segments = transcripts[recId] ?? []
                                                    return (
                                                        <Stack key={recId} gap={0.75}>
                                                            <Stack direction="row" alignItems="center" gap={1}>
                                                                <Typography
                                                                    level="body-xs"
                                                                    sx={{
                                                                        fontFamily: 'monospace',
                                                                        color: 'text.secondary',
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                    }}
                                                                >
                                                                    {recId}
                                                                </Typography>
                                                                <Chip
                                                                    size="sm"
                                                                    variant="soft"
                                                                    color={RECORDING_STATUS_COLORS[rec.status] ?? 'neutral'}
                                                                    sx={{ '--Chip-minHeight': '18px', '--Chip-paddingInline': '6px', fontSize: 10 }}
                                                                >
                                                                    {rec.status}
                                                                </Chip>
                                                                <Typography level="body-xs" color="neutral" sx={{ whiteSpace: 'nowrap' }}>
                                                                    {relativeTime(rec.created_at)}
                                                                </Typography>
                                                                <Tooltip title="Open transcript in Attio" placement="top">
                                                                    <Box
                                                                        component="a"
                                                                        href={`https://app.attio.com/numeo/calls/${call.id.meeting_id}/${recId}/transcript`}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.4,
                                                                            color: 'primary.500',
                                                                            fontSize: 11,
                                                                            textDecoration: 'none',
                                                                            whiteSpace: 'nowrap',
                                                                            '&:hover': { textDecoration: 'underline' },
                                                                        }}
                                                                    >
                                                                        Attio <OpenInNewIcon sx={{ fontSize: 11 }} />
                                                                    </Box>
                                                                </Tooltip>
                                                            </Stack>
                                                            {isLoading && <CircularProgress size="sm" />}
                                                            {hasTranscript && segments.length === 0 && !isLoading && (
                                                                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                                                    Transcript empty.
                                                                </Typography>
                                                            )}
                                                            {hasTranscript && segments.length > 0 && (
                                                                <Box
                                                                    sx={{
                                                                        bgcolor: 'background.level2',
                                                                        borderRadius: 'sm',
                                                                        p: 1.25,
                                                                        maxHeight: 280,
                                                                        overflow: 'auto',
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        level="body-xs"
                                                                        sx={{
                                                                            fontFamily: 'monospace',
                                                                            whiteSpace: 'pre-wrap',
                                                                            wordBreak: 'break-word',
                                                                            lineHeight: 1.65,
                                                                        }}
                                                                    >
                                                                        {collapseSegments(segments)}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Stack>
                                                    )
                                                })}
                                            </Stack>
                                        )}
                                    </Stack>
                                )}

                                {/* CRM pipeline detail — shown only when the call has been ingested */}
                                {crmStatus !== 'not_synced' && linkedMessages.length > 0 ? (
                                    <Stack gap={0.75}>
                                        <SectionLabel>
                                            CRM Pipeline ({linkedMessages.length} recording{linkedMessages.length !== 1 ? 's' : ''})
                                        </SectionLabel>
                                        {linkedMessages.map((msg) => {
                                            const attr = msg.external_attribution
                                            return (
                                                <Box
                                                    key={msg.entry_id}
                                                    sx={{
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 'sm',
                                                        p: 1,
                                                        bgcolor: 'background.surface',
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.75 }}>
                                                        <OracleStatusChip status={msg.status} />
                                                        <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary', fontSize: 10 }}>
                                                            {msg.entry_id}
                                                        </Typography>
                                                    </Stack>
                                                    {attr && (
                                                        <Box sx={{ fontFamily: 'monospace', fontSize: 11, lineHeight: 1.55 }}>
                                                            <Box>
                                                                company:{' '}
                                                                <Box component="span" sx={{ color: 'primary.500' }}>
                                                                    {attr.nameHint}
                                                                </Box>
                                                            </Box>
                                                            {attr.domainHint && <Box>domain: {attr.domainHint}</Box>}
                                                            {attr.externalId && <Box>externalId: {attr.externalId.slice(0, 12)}…</Box>}
                                                        </Box>
                                                    )}
                                                    {msg.structured_contacts.length > 0 && (
                                                        <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                                                            {msg.structured_contacts
                                                                .map((c) => c.email)
                                                                .filter(Boolean)
                                                                .join(', ')}
                                                        </Typography>
                                                    )}
                                                    <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary', fontVariantNumeric: 'tabular-nums' }}>
                                                        ingested {relativeTime(msg.created_at)}
                                                        {msg.processed_at ? ` · processed ${relativeTime(msg.processed_at)}` : ''}
                                                    </Typography>
                                                </Box>
                                            )
                                        })}
                                    </Stack>
                                ) : crmStatus === 'not_synced' ? (
                                    <Stack gap={0.5}>
                                        <SectionLabel>CRM Pipeline</SectionLabel>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                                            No row yet. Use the sync icon to fetch this call's transcript and mark it ready for the pipeline.
                                        </Typography>
                                    </Stack>
                                ) : null}
                            </Box>
                        </Box>
                    </td>
                </tr>
            )}
        </>
    )
}

// ── IgnoreRuleRow ──────────────────────────────────────────────────────────────

function IgnoreRuleRow({ rule, onDelete }: { rule: AttioIgnoreRule; onDelete: () => Promise<void> }) {
    const [deleting, setDeleting] = useState(false)
    const handleDelete = async () => {
        setDeleting(true)
        try {
            await onDelete()
        } finally {
            setDeleting(false)
        }
    }
    return (
        <tr>
            <td>
                <Chip size="sm" variant="soft" color={rule.type === 'title_exact' ? 'primary' : 'warning'} sx={{ fontSize: 10 }}>
                    {rule.type === 'title_exact' ? 'title' : 'meeting id'}
                </Chip>
            </td>
            <td>
                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                    {rule.value}
                </Typography>
            </td>
            <td>
                <Typography level="body-xs" color="neutral">
                    {rule.label}
                </Typography>
            </td>
            <td>
                <Typography level="body-xs" color="neutral" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {relativeTime(rule.created_at)}
                </Typography>
            </td>
            <td>
                <Tooltip title="Remove this ignore rule" placement="left">
                    <IconButton size="sm" variant="plain" color="danger" loading={deleting} onClick={handleDelete}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            </td>
        </tr>
    )
}

// ── NoteRow ────────────────────────────────────────────────────────────────────

function NoteRow({ msg }: { msg: AttioMessage }) {
    const [expanded, setExpanded] = useState(false)
    const attr = msg.external_attribution
    const contacts = msg.structured_contacts ?? []

    return (
        <>
            <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded((v) => !v)}>
                <td>
                    <Stack direction="row" alignItems="center" gap={0.75}>
                        <NoteIcon fontSize="small" sx={{ color: 'neutral.400' }} />
                        <Typography level="body-xs" color="neutral">
                            Note
                        </Typography>
                    </Stack>
                </td>
                <td>
                    {attr ? (
                        <Stack>
                            <Typography level="body-sm" fontWeight="md">
                                {attr.nameHint}
                            </Typography>
                            {attr.domainHint && (
                                <Typography level="body-xs" color="neutral">
                                    {attr.domainHint}
                                </Typography>
                            )}
                        </Stack>
                    ) : (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                            no company hint
                        </Typography>
                    )}
                </td>
                <td>
                    <Tooltip
                        title={contacts.length > 0 ? `${contacts.length} contact hint${contacts.length !== 1 ? 's' : ''}` : 'No contact hints'}
                        placement="bottom"
                    >
                        <Chip size="sm" variant="soft" color={contacts.length > 0 ? 'primary' : 'neutral'} sx={{ minWidth: 32, justifyContent: 'center' }}>
                            {contacts.length}
                        </Chip>
                    </Tooltip>
                </td>
                <td>
                    <Typography level="body-xs" color="neutral" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {relativeTime(msg.created_at)}
                    </Typography>
                </td>
                <td>
                    <OracleStatusChip status={msg.status} />
                </td>
                <td>{expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={6} style={{ padding: 0 }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.level1', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack gap={1.5}>
                                <SectionLabel>sourceId</SectionLabel>
                                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                                    {msg.entry_id}
                                </Typography>

                                <SectionLabel>externalAttribution → Step 2 Match pass 0</SectionLabel>
                                {attr ? (
                                    <Box sx={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                        <Box>
                                            system:{' '}
                                            <Box component="span" sx={{ color: 'primary.500' }}>
                                                {attr.system}
                                            </Box>
                                        </Box>
                                        <Box>
                                            externalId:{' '}
                                            <Box component="span" sx={{ color: 'primary.500' }}>
                                                {attr.externalId}
                                            </Box>
                                        </Box>
                                        <Box>nameHint: {attr.nameHint}</Box>
                                        {attr.domainHint && <Box>domainHint: {attr.domainHint}</Box>}
                                        {attr.domains && attr.domains.length > 0 && <Box>domains: [{attr.domains.join(', ')}]</Box>}
                                    </Box>
                                ) : (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        (none — pass 0 skipped)
                                    </Typography>
                                )}

                                <SectionLabel>structuredContacts → Step 2 Match passes 1-2</SectionLabel>
                                {contacts.length === 0 ? (
                                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                        (no contact hints)
                                    </Typography>
                                ) : (
                                    <Box sx={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6 }}>
                                        {contacts.map((c, i) => (
                                            <Box key={i}>
                                                {c.email && <Box component="span">{c.email}</Box>}
                                                {c.phone && (
                                                    <Box component="span" sx={{ ml: c.email ? 1 : 0, color: 'neutral.500' }}>
                                                        {c.phone}
                                                    </Box>
                                                )}
                                                {c.name && (
                                                    <Box component="span" sx={{ ml: 1, color: 'neutral.500' }}>
                                                        · {c.name}
                                                    </Box>
                                                )}
                                                {c.title && (
                                                    <Box component="span" sx={{ ml: 1, color: 'neutral.500', fontStyle: 'italic' }}>
                                                        {c.title}
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <SectionLabel>formatted_text → fed into Gemini Extract</SectionLabel>
                                <Typography level="body-xs" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {msg.formatted_text || '(no content)'}
                                </Typography>
                            </Stack>
                        </Box>
                    </td>
                </tr>
            )}
        </>
    )
}

// ── AttioPanel ─────────────────────────────────────────────────────────────────

interface AttioPanelProps {
    onBack?: () => void
}

export function AttioPanel({ onBack }: AttioPanelProps) {
    const [status, setStatus] = useState<AttioStatus | null>(null)
    const [messages, setMessages] = useState<AttioMessage[]>([])
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
    const [showIgnored, setShowIgnored] = useState(false)
    const [showNoRecording, setShowNoRecording] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [retrying, setRetrying] = useState(false)
    const [retryResult, setRetryResult] = useState('')
    const [resetting, setResetting] = useState(false)
    const [wiping, setWiping] = useState(false)
    const [wipeResult, setWipeResult] = useState('')

    const today = new Date().toISOString().slice(0, 10)
    const defaultStart = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    const [resetSince, setResetSince] = useState(defaultStart)
    const [resetUntil, setResetUntil] = useState(today)
    const [useEndDate, setUseEndDate] = useState(true)
    const [etlOpen, setEtlOpen] = useState(false)
    const [resyncOpen, setResyncOpen] = useState(false)
    const resyncRef = useRef<HTMLDivElement>(null)
    const [resyncAnchorRect, setResyncAnchorRect] = useState<{ top: number; right: number } | null>(null)

    const [calls, setCalls] = useState<AttioCallItem[]>([])
    const [searchParams, setSearchParams] = useSearchParams()
    const callsStartsBefore = searchParams.get('callsEnd') ?? new Date().toISOString()
    const setCallsStartsBefore = useCallback(
        (next: string) => {
            setSearchParams(
                (prev) => {
                    const p = new URLSearchParams(prev)
                    p.set('callsEnd', next.slice(0, 10))
                    return p
                },
                { replace: true }
            )
        },
        [setSearchParams]
    )
    const [callsLoading, setCallsLoading] = useState(false)
    const [callsError, setCallsError] = useState('')
    // meetingId → true (has completed recording) | false (none) | 'loading'
    const [candidacyMap, setCandidacyMap] = useState<Map<string, boolean | 'loading'>>(new Map())

    const [notesExpanded, setNotesExpanded] = useState(false)
    const [ignoreRules, setIgnoreRules] = useState<AttioIgnoreRule[]>([])
    const [ignoreRulesExpanded, setIgnoreRulesExpanded] = useState(false)

    // The header is inside an `overflowX: auto` container which clips absolutely-
    // positioned elements. Portal the popover to document.body and pin via
    // fixed coords derived from the button's bounding rect.
    useLayoutEffect(() => {
        if (!resyncOpen) return
        const update = () => {
            const el = resyncRef.current
            if (!el) return
            const r = el.getBoundingClientRect()
            setResyncAnchorRect({ top: r.bottom + 6, right: window.innerWidth - r.right })
        }
        update()
        window.addEventListener('scroll', update, true)
        window.addEventListener('resize', update)
        return () => {
            window.removeEventListener('scroll', update, true)
            window.removeEventListener('resize', update)
        }
    }, [resyncOpen])

    useEffect(() => {
        if (!resyncOpen) return
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node
            if (resyncRef.current?.contains(t)) return
            const popover = document.getElementById('attio-resync-popover')
            if (popover?.contains(t)) return
            setResyncOpen(false)
        }
        document.addEventListener('mousedown', onDown)
        return () => document.removeEventListener('mousedown', onDown)
    }, [resyncOpen])

    const fetchAll = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const [s, msgs, rules] = await Promise.all([getAttioStatus(), listAttioMessages({ limit: 200 }), listAttioIgnoreRules()])
            setStatus(s)
            setMessages(msgs)
            setIgnoreRules(rules)
        } catch (err) {
            setError((err as Error).message || 'Failed to load Attio data')
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void fetchAll()
    }, [fetchAll])

    const fetchCalls = useCallback(async (startsBefore: string) => {
        setCallsLoading(true)
        setCallsError('')
        try {
            const endsFrom = new Date(new Date(startsBefore).getTime() - CALLS_WINDOW_DAYS * 86_400_000).toISOString()
            const allItems: AttioCallItem[] = []
            let cursor: string | undefined
            do {
                const result = await listAttioCalls({ ends_from: endsFrom, starts_before: startsBefore, cursor })
                allItems.push(...result.items)
                cursor = result.nextCursor ?? undefined
            } while (cursor)
            setCalls(allItems)
        } catch (err) {
            setCallsError((err as Error).message || 'Failed to load calls')
        } finally {
            setCallsLoading(false)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        void fetchCalls(callsStartsBefore)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Prefetch recordings for all visible calls to determine pipeline candidacy.
    // Batched in groups of 10 to stay within Attio rate limits.
    useEffect(() => {
        if (calls.length === 0) {
            setCandidacyMap(new Map())
            return
        }
        setCandidacyMap(new Map(calls.map((c) => [c.id.meeting_id, 'loading'])))
        let cancelled = false
        const BATCH_SIZE = 10
        const run = async () => {
            for (let i = 0; i < calls.length; i += BATCH_SIZE) {
                if (cancelled) break
                await Promise.all(
                    calls.slice(i, i + BATCH_SIZE).map(async (call) => {
                        try {
                            const recs = await getAttioCallRecordings(call.id.meeting_id)
                            if (!cancelled)
                                setCandidacyMap((prev) =>
                                    new Map(prev).set(
                                        call.id.meeting_id,
                                        recs.some((r) => r.status === 'completed')
                                    )
                                )
                        } catch {
                            if (!cancelled) setCandidacyMap((prev) => new Map(prev).set(call.id.meeting_id, false))
                        }
                    })
                )
            }
        }
        void run()
        return () => {
            cancelled = true
        }
    }, [calls])

    const handleCallsOlder = () => {
        const rawNext = endTs - windowMs
        const clamped = new Date(Math.max(rawNext, EARLIEST_DATE.getTime() + windowMs)).toISOString()
        setCallsStartsBefore(clamped)
        void fetchCalls(clamped)
    }

    const handleCallsNewer = () => {
        const shifted = new Date(new Date(callsStartsBefore).getTime() + CALLS_WINDOW_DAYS * 86_400_000)
        const now = new Date()
        const next = shifted > now ? now.toISOString() : shifted.toISOString()
        setCallsStartsBefore(next)
        void fetchCalls(next)
    }

    const windowMs = CALLS_WINDOW_DAYS * 86_400_000
    const endTs = new Date(callsStartsBefore).getTime()
    const startTs = endTs - windowMs
    const newerEndTs = Math.min(endTs + windowMs, Date.now())

    const isAtNewest = endTs >= Date.now() - 60_000
    const isAtOldest = startTs <= EARLIEST_DATE.getTime()

    const currentWindowLabel = fmtWindowRange(new Date(startTs), new Date(endTs))
    const newerWindowLabel = fmtWindowRange(new Date(endTs), new Date(newerEndTs))
    const olderWindowLabel = fmtWindowRange(new Date(startTs - windowMs), new Date(startTs))

    // YYYY-MM-DD value for the date input; represents the window end (callsStartsBefore)
    const dateInputValue = new Date(callsStartsBefore).toISOString().slice(0, 10)
    const dateInputMin = new Date(EARLIEST_DATE.getTime() + windowMs).toISOString().slice(0, 10)
    const dateInputMax = new Date().toISOString().slice(0, 10)

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value
        if (!val) return
        // Parse as end-of-day UTC to avoid timezone shifting the selected date
        const picked = new Date(val + 'T23:59:59.999Z').getTime()
        const minTs = EARLIEST_DATE.getTime() + windowMs
        const maxTs = Date.now()
        const clamped = new Date(Math.min(Math.max(picked, minTs), maxTs)).toISOString()
        setCallsStartsBefore(clamped)
        void fetchCalls(clamped)
    }

    const { startJob, isRunning } = useProcessJobContext()

    const handleSync = async () => {
        setError('')
        try {
            await startJob({ source: 'attio' })
        } catch (err) {
            setError((err as Error).message || 'Sync failed')
        }
    }

    const handleRetry = async () => {
        setRetrying(true)
        setRetryResult('')
        setError('')
        try {
            const result = await retryAttioMessages()
            setRetryResult(`Reset ${result.reset} message${result.reset !== 1 ? 's' : ''} to pending`)
            await fetchAll()
        } catch (err) {
            setError((err as Error).message || 'Retry failed')
        } finally {
            setRetrying(false)
        }
    }

    const handleResyncConfirm = async () => {
        setResetting(true)
        setError('')
        try {
            const from = `${resetSince}T00:00:00Z`
            const to = useEndDate && resetUntil < today ? `${resetUntil}T23:59:59Z` : new Date().toISOString()
            const result = await prepAttioCalls({ from, to })
            await fetchAll()
            setResyncOpen(false)
            setError(`Prep complete: ${result.prepped} ready, ${result.jibberish} jibberish, ${result.skipped} skipped`)
        } catch (err) {
            setError((err as Error).message || 'Prep failed')
        } finally {
            setResetting(false)
        }
    }

    const handleWipeAll = async () => {
        if (!window.confirm('Delete ALL companies, facts, contacts, messages, and the File Search index? This cannot be undone.')) return
        setWiping(true)
        setWipeResult('')
        setError('')
        try {
            const result = await resetAttioData()
            const total = Object.values(result.counts).reduce((s, n) => s + n, 0)
            setWipeResult(`Wiped ${total} records across ${Object.keys(result.counts).length} collections`)
            await fetchAll()
        } catch (err) {
            setError((err as Error).message || 'Wipe failed')
        } finally {
            setWiping(false)
        }
    }

    const handleIgnore = useCallback(async (type: AttioIgnoreRuleType, value: string, label: string) => {
        await createAttioIgnoreRule({ type, value, label })
        const rules = await listAttioIgnoreRules()
        setIgnoreRules(rules)
    }, [])

    const handleRowSync = useCallback(
        async (meetingId: string, force: boolean) => {
            setError('')
            try {
                const result = await syncAttioCall(meetingId, force)
                if (result.entryIds.length === 0) {
                    setError(`Nothing to ingest for this call (${result.skipped.join(', ') || 'no transcript available'})`)
                }
                await fetchAll()
            } catch (err) {
                setError((err as Error).message || 'Sync failed')
            }
        },
        [fetchAll]
    )

    const ignoredMeetingIds = useMemo(() => new Set(ignoreRules.filter((r) => r.type === 'meeting_id').map((r) => r.value)), [ignoreRules])
    const ignoredTitles = useMemo(() => new Set(ignoreRules.filter((r) => r.type === 'title_exact').map((r) => r.value.toLowerCase())), [ignoreRules])

    const isCallIgnored = useCallback(
        (call: AttioCallItem) => ignoredMeetingIds.has(call.id.meeting_id) || (!!call.title && ignoredTitles.has(call.title.toLowerCase())),
        [ignoredMeetingIds, ignoredTitles]
    )

    // Build a fast lookup: meetingId → list of crm_attio_messages rows.
    const messagesByMeetingId = useMemo(() => {
        const map = new Map<string, AttioMessage[]>()
        for (const msg of messages) {
            if (msg.entry_type !== 'call_transcript') continue
            const meetingId = meetingIdFromEntryId(msg.entry_id)
            if (!meetingId) continue
            const arr = map.get(meetingId) ?? []
            arr.push(msg)
            map.set(meetingId, arr)
        }
        return map
    }, [messages])

    const noteMessages = useMemo(() => messages.filter((m) => m.entry_type === 'note'), [messages])

    // Filter calls by the selected CRM status. Ignored calls and calls with no
    // completed recording are hidden by default — use the toggles to show them.
    const filteredCalls = useMemo(() => {
        return calls.filter((call) => {
            if (!showIgnored && isCallIgnored(call)) return false
            const linked = messagesByMeetingId.get(call.id.meeting_id) ?? []
            const base = computeCallOracleStatus(linked)
            const isNoRecording = base === 'not_synced' && candidacyMap.get(call.id.meeting_id) === false
            if (!showNoRecording && isNoRecording) return false
            if (statusFilter !== 'all') {
                const effective: EffectiveOracleStatus = isNoRecording ? 'no_recording' : base
                if (effective !== statusFilter) return false
            }
            return true
        })
    }, [calls, statusFilter, messagesByMeetingId, showIgnored, showNoRecording, isCallIgnored, candidacyMap])

    const ignoredCallsHiddenCount = useMemo(() => {
        if (showIgnored) return 0
        let n = 0
        for (const call of calls) if (isCallIgnored(call)) n++
        return n
    }, [calls, showIgnored, isCallIgnored])

    const noRecordingHiddenCount = useMemo(() => {
        if (showNoRecording) return 0
        let n = 0
        for (const call of calls) {
            if (isCallIgnored(call)) continue
            const linked = messagesByMeetingId.get(call.id.meeting_id) ?? []
            if (computeCallOracleStatus(linked) === 'not_synced' && candidacyMap.get(call.id.meeting_id) === false) n++
        }
        return n
    }, [calls, showNoRecording, isCallIgnored, messagesByMeetingId, candidacyMap])

    // Filter notes by status (notes never have "not_synced" — they're always in our system).
    const filteredNotes = useMemo(() => {
        if (statusFilter === 'all' || statusFilter === 'not_synced') return noteMessages
        return noteMessages.filter((m) => m.status === statusFilter)
    }, [noteMessages, statusFilter])

    // Count how many currently-loaded calls have no CRM entry (useful for filter pill label).
    const notSyncedCount = useMemo(
        () => calls.filter((c) => (messagesByMeetingId.get(c.id.meeting_id) ?? []).length === 0).length,
        [calls, messagesByMeetingId]
    )

    const dateRangeInvalid = useEndDate && resetUntil < resetSince
    const endDateInFuture = useEndDate && resetUntil > today
    const failedCount = status?.counts.failed ?? 0
    const unattributedCount = status?.counts.unattributed ?? 0

    return (
        <Stack gap={1.5} sx={{ height: '100%', minHeight: 0 }}>
            {/* ── Header: title · counts · cursors · actions ── */}
            <Stack direction="row" alignItems="center" gap={1.25} sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}>
                {onBack && (
                    <Tooltip title="Back to pipeline">
                        <IconButton size="sm" variant="plain" color="neutral" onClick={onBack}>
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Typography level="title-md" sx={{ whiteSpace: 'nowrap' }}>
                    Attio Data Source
                </Typography>
                {status && (
                    <Chip size="sm" color={status.cursors.meetings_cursor ? 'success' : 'neutral'} variant="soft">
                        {status.cursors.meetings_cursor ? 'Active' : 'No syncs'}
                    </Chip>
                )}

                <Divider orientation="vertical" />
                {status && <InlineCounts counts={status.counts} />}

                <Divider orientation="vertical" />
                {status && <InlineCursors cursors={status.cursors} />}

                <Box sx={{ flex: 1 }} />

                <Tooltip title="Developer: wipe Attio messages and sync cursor" placement="bottom">
                    <IconButton size="sm" variant="plain" color="danger" loading={wiping} onClick={handleWipeAll}>
                        <DeleteForeverIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>

                <Stack direction="row" gap={0.75} alignItems="center">
                    <Box ref={resyncRef}>
                        <Tooltip title="Re-fetch data from a specific date range" placement="bottom">
                            <Button
                                size="sm"
                                variant="outlined"
                                color="neutral"
                                startDecorator={<CalendarTodayIcon sx={{ fontSize: 15 }} />}
                                endDecorator={resyncOpen ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
                                onClick={() => setResyncOpen((v) => !v)}
                            >
                                Prep for pipeline
                            </Button>
                        </Tooltip>

                        {resyncOpen &&
                            resyncAnchorRect &&
                            createPortal(
                                <Sheet
                                    id="attio-resync-popover"
                                    variant="outlined"
                                    sx={{
                                        position: 'fixed',
                                        top: resyncAnchorRect.top,
                                        right: resyncAnchorRect.right,
                                        zIndex: 1300,
                                        borderRadius: 'sm',
                                        p: 2,
                                        minWidth: 280,
                                        boxShadow: 'md',
                                    }}
                                >
                                    <Stack gap={1.5}>
                                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
                                            <Stack gap={0.25}>
                                                <Typography level="title-sm">Prep calls for pipeline</Typography>
                                                <Typography level="body-xs" color="neutral">
                                                    Fetch Attio calls in this date range, run transcript quality check, and write ready rows for the CRM
                                                    pipeline.
                                                </Typography>
                                            </Stack>
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="neutral"
                                                onClick={() => setResyncOpen(false)}
                                                sx={{ mt: -0.5, mr: -0.5 }}
                                            >
                                                <CloseIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Stack>
                                        <Divider />
                                        <Stack gap={0.5}>
                                            <FormLabel sx={{ fontSize: 12 }}>From</FormLabel>
                                            <input
                                                type="date"
                                                value={resetSince}
                                                max={today}
                                                onChange={(e) => setResetSince(e.target.value)}
                                                style={{
                                                    fontSize: 13,
                                                    padding: '6px 8px',
                                                    borderRadius: 6,
                                                    border: '1px solid var(--joy-palette-neutral-300)',
                                                    width: '100%',
                                                    boxSizing: 'border-box',
                                                }}
                                            />
                                        </Stack>
                                        <Stack gap={0.5}>
                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                <FormLabel sx={{ fontSize: 12 }}>To</FormLabel>
                                                <Typography
                                                    level="body-xs"
                                                    color={useEndDate ? 'primary' : 'neutral'}
                                                    sx={{ cursor: 'pointer', textDecoration: 'underline', fontSize: 11 }}
                                                    onClick={() => setUseEndDate((v) => !v)}
                                                >
                                                    {useEndDate ? 'Sync up to today instead' : 'Set custom end date'}
                                                </Typography>
                                            </Stack>
                                            {useEndDate ? (
                                                <input
                                                    type="date"
                                                    value={resetUntil}
                                                    min={resetSince}
                                                    max={today}
                                                    onChange={(e) => setResetUntil(e.target.value)}
                                                    style={{
                                                        fontSize: 13,
                                                        padding: '6px 8px',
                                                        borderRadius: 6,
                                                        border: `1px solid ${dateRangeInvalid ? 'var(--joy-palette-danger-400)' : 'var(--joy-palette-neutral-300)'}`,
                                                        width: '100%',
                                                        boxSizing: 'border-box',
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    level="body-sm"
                                                    sx={{ color: 'text.tertiary', py: 0.75, px: 1, borderRadius: 6, bgcolor: 'background.level1' }}
                                                >
                                                    Today ({today})
                                                </Typography>
                                            )}
                                            <FormHelperText sx={{ fontSize: 11, color: dateRangeInvalid ? 'danger.500' : undefined }}>
                                                {dateRangeInvalid
                                                    ? 'End date must be on or after start date'
                                                    : endDateInFuture
                                                      ? 'End date is in the future — will be capped at today'
                                                      : useEndDate
                                                        ? 'Only data within this range will be synced.'
                                                        : 'All data from start date through today will be synced.'}
                                            </FormHelperText>
                                        </Stack>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            variant="solid"
                                            loading={resetting || isRunning}
                                            disabled={dateRangeInvalid || isRunning}
                                            onClick={handleResyncConfirm}
                                        >
                                            {isRunning ? 'Sync already running' : 'Start prep'}
                                        </Button>
                                    </Stack>
                                </Sheet>,
                                document.body
                            )}
                    </Box>

                    {(failedCount > 0 || unattributedCount > 0) && (
                        <Tooltip
                            title={`Re-queue ${failedCount + unattributedCount} failed/unattributed message${failedCount + unattributedCount !== 1 ? 's' : ''} back to pending`}
                            placement="bottom"
                        >
                            <Button
                                size="sm"
                                variant="soft"
                                color="warning"
                                startDecorator={<ReplayIcon sx={{ fontSize: 15 }} />}
                                loading={retrying}
                                onClick={handleRetry}
                            >
                                Retry {failedCount + unattributedCount}
                            </Button>
                        </Tooltip>
                    )}

                    <Button
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        startDecorator={<ScienceIcon sx={{ fontSize: 15 }} />}
                        onClick={() => setEtlOpen(true)}
                    >
                        Dry Run ETL
                    </Button>

                    <Button size="sm" startDecorator={<SyncIcon sx={{ fontSize: 15 }} />} loading={isRunning} onClick={handleSync} disabled={isRunning}>
                        Sync Now
                    </Button>

                    <Tooltip title="Refresh status and messages" placement="bottom">
                        <IconButton size="sm" variant="plain" color="neutral" onClick={fetchAll}>
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            {error && <Alert color="danger">{error}</Alert>}
            {retryResult && <Alert color="success">{retryResult}</Alert>}
            {wipeResult && <Alert color="success">{wipeResult}</Alert>}

            {/* ── Status filter pills + ignored toggle ── */}
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <StatusFilterPills value={statusFilter} onChange={setStatusFilter} notSyncedCount={notSyncedCount} />
                <Box sx={{ flex: 1 }} />
                <Tooltip
                    title={showNoRecording ? 'Hide calls with no recording' : 'Show calls with no completed recording (will never enter the pipeline)'}
                    placement="top"
                >
                    <Button
                        size="sm"
                        variant={showNoRecording ? 'soft' : 'outlined'}
                        color="neutral"
                        startDecorator={<BlockIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setShowNoRecording((v) => !v)}
                    >
                        {showNoRecording ? 'Hide no recording' : `Show no recording${noRecordingHiddenCount > 0 ? ` · ${noRecordingHiddenCount}` : ''}`}
                    </Button>
                </Tooltip>
                <Tooltip title={showIgnored ? 'Hide ignored calls from the list' : 'Show ignored calls in the list'} placement="top">
                    <Button
                        size="sm"
                        variant={showIgnored ? 'soft' : 'outlined'}
                        color="neutral"
                        startDecorator={<BlockIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setShowIgnored((v) => !v)}
                    >
                        {showIgnored ? 'Hide ignored' : `Show ignored${ignoredCallsHiddenCount > 0 ? ` · ${ignoredCallsHiddenCount}` : ''}`}
                    </Button>
                </Tooltip>
            </Stack>

            {/* ── Calls table ── */}
            <Stack gap={1} sx={{ flex: 1, minHeight: 0 }}>
                {callsError && <Alert color="danger">{callsError}</Alert>}

                {callsLoading ? (
                    <Stack alignItems="center" py={4}>
                        <CircularProgress />
                    </Stack>
                ) : filteredCalls.length === 0 ? (
                    <>
                        <Typography level="body-sm" color="neutral" sx={{ py: 4, textAlign: 'center', fontStyle: 'italic' }}>
                            {statusFilter === 'all'
                                ? ignoredCallsHiddenCount > 0
                                    ? `No calls visible — ${ignoredCallsHiddenCount} ignored call${ignoredCallsHiddenCount !== 1 ? 's' : ''} hidden on this page. Click "Show ignored" to view.`
                                    : 'No calls in this window.'
                                : `No calls with status "${STATUS_META[statusFilter as EffectiveOracleStatus]?.label ?? statusFilter}" in this window.`}
                        </Typography>
                        {/* Show navigator even on empty pages so users can move to adjacent windows */}
                        <Stack direction="row" alignItems="center" justifyContent="center" gap={0.5}>
                            <Tooltip title={isAtOldest ? `Can't go before Jan 1, 2025` : `Go to ${olderWindowLabel}`} placement="top">
                                <span>
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        disabled={isAtOldest}
                                        onClick={handleCallsOlder}
                                        sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs' }}
                                    >
                                        ← {olderWindowLabel}
                                    </Button>
                                </span>
                            </Tooltip>
                            <Input
                                type="date"
                                size="sm"
                                value={dateInputValue}
                                onChange={handleDateChange}
                                slotProps={{ input: { min: dateInputMin, max: dateInputMax } }}
                                sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs', width: 140 }}
                            />
                            <Tooltip title={isAtNewest ? 'Already at the most recent window' : `Go to ${newerWindowLabel}`} placement="top">
                                <span>
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        disabled={isAtNewest}
                                        onClick={handleCallsNewer}
                                        sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs' }}
                                    >
                                        {newerWindowLabel} →
                                    </Button>
                                </span>
                            </Tooltip>
                        </Stack>
                    </>
                ) : (
                    <>
                        <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', flex: 1, minHeight: 0 }}>
                            <Table stickyHeader hoverRow>
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th style={{ width: 160 }}>Start</th>
                                        <th style={{ width: 80 }}>Scheduled</th>
                                        <th style={{ width: 100 }}>Participants</th>
                                        <th style={{ width: 160 }}>Company</th>
                                        <th style={{ width: 150 }}>
                                            <Tooltip
                                                title="CRM pipeline status: not synced = not yet fetched into crm_attio_messages | pending = waiting for process job | processed = facts written | unattributed = no company match | failed = error"
                                                placement="bottom"
                                            >
                                                <span>CRM Status</span>
                                            </Tooltip>
                                        </th>
                                        <th style={{ width: 40 }} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCalls.map((call) => (
                                        <CallRow
                                            key={call.id.meeting_id}
                                            call={call}
                                            linkedMessages={messagesByMeetingId.get(call.id.meeting_id) ?? []}
                                            isIgnored={isCallIgnored(call)}
                                            isCandidate={candidacyMap.get(call.id.meeting_id) ?? 'loading'}
                                            onIgnore={handleIgnore}
                                            onSync={handleRowSync}
                                        />
                                    ))}
                                </tbody>
                            </Table>
                        </Sheet>

                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Typography level="body-xs" color="neutral">
                                {filteredCalls.length} call{filteredCalls.length !== 1 ? 's' : ''}
                                {statusFilter !== 'all' && calls.length !== filteredCalls.length ? ` (${calls.length} total, filtered)` : ''}
                            </Typography>
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                <Tooltip title={isAtOldest ? `Can't go before Jan 1, 2025` : `Go to ${olderWindowLabel}`} placement="top">
                                    <span>
                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            disabled={isAtOldest}
                                            onClick={handleCallsOlder}
                                            sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs' }}
                                        >
                                            ← {olderWindowLabel}
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Input
                                    type="date"
                                    size="sm"
                                    value={dateInputValue}
                                    onChange={handleDateChange}
                                    slotProps={{ input: { min: dateInputMin, max: dateInputMax } }}
                                    sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs', width: 140 }}
                                />
                                <Tooltip title={isAtNewest ? 'Already at the most recent window' : `Go to ${newerWindowLabel}`} placement="top">
                                    <span>
                                        <Button
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            disabled={isAtNewest}
                                            onClick={handleCallsNewer}
                                            sx={{ fontVariantNumeric: 'tabular-nums', fontSize: 'xs' }}
                                        >
                                            {newerWindowLabel} →
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Stack>
                    </>
                )}

                {/* ── Notes section ── */}
                {filteredNotes.length > 0 && (
                    <Stack gap={0.5}>
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{ cursor: 'pointer', py: 0.5 }} onClick={() => setNotesExpanded((v) => !v)}>
                            {notesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            <Typography level="title-sm">Notes</Typography>
                            <Chip size="sm" variant="soft" color="neutral">
                                {filteredNotes.length}
                            </Chip>
                            <Typography level="body-xs" color="neutral">
                                — Attio notes ingested into the CRM pipeline
                            </Typography>
                        </Stack>

                        {notesExpanded && (
                            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                                <Table stickyHeader hoverRow>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 80 }}>Type</th>
                                            <th>Company hint</th>
                                            <th style={{ width: 80 }}>Contacts</th>
                                            <th style={{ width: 110 }}>Ingested</th>
                                            <th style={{ width: 150 }}>CRM Status</th>
                                            <th style={{ width: 40 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredNotes.map((msg) => (
                                            <NoteRow key={msg.entry_id} msg={msg} />
                                        ))}
                                    </tbody>
                                </Table>
                            </Sheet>
                        )}
                    </Stack>
                )}

                {/* ── Ignored rules section ── */}
                <Stack gap={0.5}>
                    <Stack direction="row" alignItems="center" gap={0.75} sx={{ cursor: 'pointer', py: 0.5 }} onClick={() => setIgnoreRulesExpanded((v) => !v)}>
                        {ignoreRulesExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <BlockIcon sx={{ fontSize: 16, color: ignoreRules.length > 0 ? 'danger.500' : 'text.tertiary' }} />
                        <Typography level="title-sm">Ignored Rules</Typography>
                        {ignoreRules.length > 0 && (
                            <Chip size="sm" variant="soft" color="danger">
                                {ignoreRules.length}
                            </Chip>
                        )}
                        <Typography level="body-xs" color="neutral">
                            — calls matching these rules are never ingested into the CRM pipeline
                        </Typography>
                    </Stack>

                    {ignoreRulesExpanded &&
                        (ignoreRules.length === 0 ? (
                            <Typography level="body-xs" color="neutral" sx={{ py: 1, fontStyle: 'italic' }}>
                                No ignore rules yet. Click the <BlockIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> button on any call to add one.
                            </Typography>
                        ) : (
                            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                                <Table stickyHeader hoverRow>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 100 }}>Type</th>
                                            <th>Value</th>
                                            <th>Label</th>
                                            <th style={{ width: 110 }}>Created</th>
                                            <th style={{ width: 48 }} />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ignoreRules.map((rule) => (
                                            <IgnoreRuleRow
                                                key={rule.id}
                                                rule={rule}
                                                onDelete={async () => {
                                                    await deleteAttioIgnoreRule(rule.id)
                                                    setIgnoreRules((prev) => prev.filter((r) => r.id !== rule.id))
                                                }}
                                            />
                                        ))}
                                    </tbody>
                                </Table>
                            </Sheet>
                        ))}
                </Stack>

                {loading && messages.length === 0 && (
                    <Stack alignItems="center" py={2}>
                        <CircularProgress size="sm" />
                    </Stack>
                )}
            </Stack>

            <EtlPreviewModal open={etlOpen} onClose={() => setEtlOpen(false)} onRunPreview={previewAttioBatch} />
        </Stack>
    )
}
